import type { User } from "@/types/fleet";
import { ApiClientError, apiRequest } from "@/lib/api/http";
import {
  ALL_FEATURES_ON,
  parseClientFeatures,
  type ClientFeature,
} from "@/lib/fleet/client-features";

interface RemoteAuthUser {
  id?: string;
  title?: string;
  username?: string;
  email?: string;
  client_id?: string;
  /** `"1"` / `"0"`. See the note on `normalizeUser` — not shipped upstream yet. */
  is_admin?: string;
  /** Not in `API.md`'s Login/Me samples, so its shape is read leniently by `parsePermissions`. */
  client_permissions?: unknown;
  /** Embedded client record (`Me.php`); only its `features` is read here. */
  client?: { features?: unknown } | null;
}

/**
 * `client_permissions` is referenced by `docs/COSTS_API_SERVER.md` but its wire shape is not
 * documented, so accept the plausible ones: an array, a JSON-encoded array/object, a
 * comma-separated string, or a `{ costs: "1" }` map. Anything else counts as "not sent".
 */
function parsePermissions(raw: unknown): string[] | null {
  if (raw === null || raw === undefined) return null;
  if (Array.isArray(raw)) {
    return raw.map((entry) => String(entry).trim()).filter(Boolean);
  }
  if (typeof raw === "object") {
    return Object.entries(raw as Record<string, unknown>)
      .filter(([, value]) => value === true || value === 1 || value === "1")
      .map(([key]) => key.trim());
  }
  if (typeof raw === "string") {
    const text = raw.trim();
    if (text.startsWith("[") || text.startsWith("{")) {
      try {
        return parsePermissions(JSON.parse(text));
      } catch {
        return null;
      }
    }
    return text.split(/[\s,]+/).filter(Boolean);
  }
  return null;
}

/**
 * Whether the user may write a resource guarded by `permission` upstream.
 *
 * Deliberately permissive in the same way as `role`: a user whose payload carries no
 * `client_permissions` at all is allowed, because hiding every edit control the day the field is
 * missing would be worse than a `403 forbidden` toast. Only a list that is present and lacks the
 * permission hides the controls. The upstream `403` remains the real enforcement.
 */
export function hasClientPermission(user: User | null, permission: string): boolean {
  if (!user) return false;
  if (user.permissions === null) return true;
  return user.permissions.includes(permission);
}

/**
 * Whether the user's client has `feature` fitted (`docs/CLIENT_FEATURES_SERVER.md`). Hiding only —
 * no data is withheld — so a user not loaded yet reads as all-on rather than flashing controls away.
 */
export function hasClientFeature(user: User | null, feature: ClientFeature): boolean {
  return (user?.features ?? ALL_FEATURES_ON)[feature];
}

function normalizeUser(raw: RemoteAuthUser): User {
  const displayName = raw.title?.trim() || raw.username?.trim() || raw.email?.trim() || "User";
  return {
    id: raw.id ?? "0",
    email: raw.email ?? "",
    name: displayName,
    username: raw.username?.trim() ?? "",
    clientId: raw.client_id?.trim() ?? "",
    // `Me.php` carries no role today (`docs/ACTIVITY_API.md` specs `is_admin`), so an absent
    // field means "upstream has not shipped it" rather than "not an admin" — treating it as the
    // latter would hide the activity page from everyone the day this lands. Only an explicit
    // "0" demotes. Deliberately permissive; tighten it once the field is live.
    role: raw.is_admin === "0" ? "viewer" : "admin",
    permissions: parsePermissions(raw.client_permissions),
    features: parseClientFeatures(raw.client?.features),
    lastLogin: new Date().toISOString(),
  };
}

interface LoginResponse {
  success: boolean;
  user: RemoteAuthUser | null;
  token: string;
  /** Some upstream builds put it beside `user` rather than inside it. */
  client_permissions?: unknown;
  /** `Login.php` puts the client record beside `user`, unlike `Me.php`. */
  client?: { features?: unknown } | null;
}

export type AuthSession = {
  user: User;
  token: string;
};

export async function loginRequest(username: string, password: string): Promise<AuthSession> {
  const response = await apiRequest<LoginResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });

  if (typeof response.token !== "string" || !response.token) {
    throw new ApiClientError("invalid_response", "Missing token in login response", 502);
  }

  return {
    user: normalizeUser({
      ...response.user,
      client_permissions: response.user?.client_permissions ?? response.client_permissions,
      client: response.user?.client ?? response.client,
    }),
    token: response.token,
  };
}

export async function meRequest(): Promise<AuthSession> {
  const response = await apiRequest<Record<string, unknown>>("/api/auth/me", {
    method: "GET",
    cache: "no-store",
  });

  const { token: rawToken, ...userFields } = response;
  const token = typeof rawToken === "string" ? rawToken : "";
  if (!token) {
    throw new ApiClientError("invalid_response", "Missing token in session response", 502);
  }

  return {
    user: normalizeUser(userFields as RemoteAuthUser),
    token,
  };
}

export async function logoutRequest(): Promise<void> {
  await apiRequest<{ success: boolean }>("/api/auth/logout", {
    method: "POST",
  });
}

